import type React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import * as api from '@gitbook/api-client';
import { getEmptyEditorValue } from '@gitbook/doc-core';
import type { Value } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import {
    Actions,
    Button,
    Description,
    Form,
    Group,
    Label,
    Paper,
    Popover,
    PopoverCloseButton,
    PopoverRoot,
    PopoverTrigger,
    Radio,
    RadioCheck,
    RadioGroup,
    RadioGroupField,
    Submit,
    toast,
    usePopoverClose,
} from '@gitbook/spine-react';

import { apiClient } from '../../state';
import { encodeDocumentForAPI, RichTextarea, RichTextareaField } from '../BasicEditor';

/**
 * Button to submit a review for a change request.
 * Showing a popover with the review form.
 */
export function ChangeRequestReviewButton(props: {
    space: string;
    changeRequest: string;
    className?: string;
}): React.ReactElement {
    const { space, changeRequest, className } = props;

    return (
        <PopoverRoot>
            <PopoverTrigger>
                <Button
                    label="Submit review"
                    kind="secondary"
                    size="medium"
                    className={className}
                    trailingIcon={Icon.ChevronDown}
                />
            </PopoverTrigger>
            <Popover placement="bottom-end">
                <Paper className="w-112 p-4">
                    <ChangeRequestReviewForm space={space} changeRequest={changeRequest} />
                </Paper>
            </Popover>
        </PopoverRoot>
    );
}

type ChangeRequestReviewFormInputs = {
    status: api.ChangeRequestReviewStatus;
    comment: Value;
};

/**
 * Form to post a review for a change request.
 */
function ChangeRequestReviewForm(props: {
    space: string;
    changeRequest: string;
}): React.ReactElement {
    const { space, changeRequest } = props;

    const form = useForm<ChangeRequestReviewFormInputs>({
        defaultValues: {
            status: api.ChangeRequestReviewStatus.Approved,
            comment: getEmptyEditorValue('change-request-review', 'basic'),
        },
    });
    const closePopover = usePopoverClose();

    const onSubmit: SubmitHandler<ChangeRequestReviewFormInputs> = async ({ status, comment }) => {
        await toast.catch(async () => {
            await apiClient.changeRequestsReviews.submitChangeRequestReview(space, changeRequest, {
                status,
                comment: comment.document.isEmpty
                    ? undefined
                    : { document: encodeDocumentForAPI(comment.document) },
            });

            closePopover();
        }, 'Error while submitting review');
    };

    return (
        <Form
            form={form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex w-full flex-col gap-6"
        >
            <RichTextareaField name="comment" required={false}>
                <Label>Finish your review</Label>
                <RichTextarea
                    placeholder="Leave a comment"
                    minRows={4}
                    withEmojis
                    withPageMentions
                    withUserMentions
                    disabled={form.formState.isSubmitting}
                    onSubmit={form.handleSubmit(onSubmit)}
                />
            </RichTextareaField>
            <RadioGroupField name="status">
                <RadioGroup aria-label="Review status" isReadOnly={form.formState.isSubmitting}>
                    <Radio
                        value={api.ChangeRequestReviewStatus.Approved}
                        className="flex items-center gap-4"
                    >
                        <RadioCheck />
                        <Group>
                            <Label>Approve</Label>
                            <Description>
                                Submit feedback and approve merging these changes.
                            </Description>
                        </Group>
                    </Radio>
                    <Radio
                        value={api.ChangeRequestReviewStatus.ChangesRequested}
                        className="flex items-center gap-4"
                    >
                        <RadioCheck />
                        <Group>
                            <Label>Request changes</Label>
                            <Description>Submit feedback suggesting changes.</Description>
                        </Group>
                    </Radio>
                </RadioGroup>
            </RadioGroupField>
            <Actions>
                <PopoverCloseButton disabled={form.formState.isSubmitting} />
                <Submit label="Submit review" disabled={false} />
            </Actions>
        </Form>
    );
}
